<template>
  <div>
    <div>
      <BaseButton
        :text="`Back to 'Your quote'`"
        flat
        ripple
        class="font-weight-bold d-flex ml-n5"
        icon="mdi-chevron-left"
        color="#106175"
        small
        @click="backToYourQuotesStep"
      />

      <div class="d-flex justify-space-between mt-10">
        <h1 class="font-24 font-lg-28 font-weight-bold primary--text">
          Your application
        </h1>
        <BaseButton
          :text="$vuetify.breakpoint.lgAndUp ? 'Save &amp; exit' : 'Save'"
          flat
          depressed
          ripple
          class="font-weight-bold px-1 py-3"
          icon="mdi-content-save"
          color="#106175"
          small
          @click="toggleGlobalSaveAndExitDialog"
        />
      </div>
    </div>

    <ValidationObserver ref="observerFormApplication" v-slot="{ validate }">
      <form @submit.prevent="onSubmit(validate)">
        <h2 class="mt-10 font-20 font-weight-bold black2-color">
          Policy details
        </h2>

        <BaseLabel :text="LABEL_CONSTANTS.BASIS_OF_ADVICE" class="mt-3 mb-2" />
        <BaseSelect
          dense
          v-model="questionBasisOfAdvice.Value"
          :items="basisOfAdviceItems"
          :data-uiid="questionBasisOfAdvice.InterfaceKey"
          :hide-details="false"
          :selectedDefault="LABEL_CONSTANTS.SELECT_DEFAULT_PLACEHOLDER"
          :rules="{
            required: {
              required: true,
              fieldLabel: LABEL_CONSTANTS.BASIS_OF_ADVICE,
            },
          }"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '280px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          aria-label="Basis of advice"
        />

        <BaseLabel
          :text="LABEL_CONSTANTS.DOCUMENT_DELIVERY_PREFERENCE"
          class="mt-3 mb-2"
        />
        <BaseToggleButton
          v-model="documentDeliveryPreferenceLocal"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
          :hide-details="false"
          :styleClass="documentDeliveryPreferenceLocal ? 'mb-10' : ''"
          :toggleButtonText="{
            yes: 'Online',
            no: 'Post',
            yesDataUiid: `${questionDocumentDeliveryPreference.InterfaceKey}_Online`,
            noDataUiid: `${questionDocumentDeliveryPreference.InterfaceKey}_Postal`,
          }"
        />
        <template v-if="questionDocumentDeliveryPreferenceIsPostal">
          <BaseLabel
            :text="LABEL_CONSTANTS.SPECIFY_CORRESPONDENCE_ADDRESS"
            class="mt-10 mb-2"
          />
          <BaseToggleButton
            v-model="questionUseCorrespondenceAddress.Value"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
            :styleClass="!questionUseCorrespondenceAddress.Value ? 'mb-10' : ''"
            :hide-details="false"
            :toggleButtonText="{
              yes: 'Yes',
              no: 'No',
              yesDataUiid: `${questionUseCorrespondenceAddress.InterfaceKey}_Yes`,
              noDataUiid: `${questionUseCorrespondenceAddress.InterfaceKey}_No`,
            }"
          />

          <div
            v-if="questionUseCorrespondenceAddress.Value"
            :class="isEnteringAddressManually ? 'mt-10' : ''"
          >
            <p
              class="sub-help-text mt-5"
              v-if="!isShowCorrespondenceAddressLabel"
            >
              {{ LABEL_CONSTANTS.SPECIFY_CORRESPONDENCE_ADDRESS_HINT_TEXT }}
            </p>
            <BaseAddressLookup
              dataUiid="CorrespondenceAddress_Find_Property"
              showManualAddressForm
              manualAddressFormText="Correspondence address"
              :placeholder="
                $vuetify.breakpoint.lgAndUp
                  ? 'Start typing your address or postcode...'
                  : 'Start typing...'
              "
              manualFormUiid="CorrespondenceAddress"
              id="address-lookup-correspondence"
              :address="correspondenceAddress"
              @address-selected="
                (response) => addressSelected(response, correspondenceAddress)
              "
              @is-address-selected="correspondenceAddressLabel"
              @use-address-finder="isEnteringAddressManually = false"
              @enter-address-manually="isEnteringAddressManually = true"
              :hideDetails="false"
              :rules="{
                required: {
                  required: true,
                  fieldLabel: 'Find your property',
                },
              }"
              :style="{
                'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
              }"
              :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            />
          </div>
        </template>

        <h2
          class="font-20 font-weight-bold black2-color"
          :class="
            isEnteringAddressManually || !isShowCorrespondenceAddressLabel
              ? 'mt-3'
              : 'mt-10'
          "
        >
          Applicant details
        </h2>

        <BaseLabel text="Phone number" class="mt-3 mb-2" />
        <BaseInput
          v-model="questionApplicant1TelNo.Value"
          :vid="questionApplicant1TelNo.InterfaceKey"
          :data-uiid="questionApplicant1TelNo.InterfaceKey"
          :hide-details="false"
          :rules="applicantPhoneNumberRules"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          aria-label="Phone number"
        />

        <BaseLabel text="Email address" class="mt-3 mb-2" />
        <BaseInput
          v-model="questionApplicant1EmailAddress.Value"
          :vid="questionApplicant1EmailAddress.InterfaceKey"
          :data-uiid="questionApplicant1EmailAddress.InterfaceKey"
          :hide-details="false"
          :rules="applicantEmailAddressRules"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          aria-label="Email address"
        />

        <template v-if="showSecondApplicantDetails">
          <h2 class="mt-3 font-20 font-weight-bold black2-color">
            Second applicant details
          </h2>

          <BaseLabel text="Phone number" class="mt-3 mb-2" />
          <BaseInput
            v-model="questionApplicant2TelNo.Value"
            :vid="questionApplicant2TelNo.InterfaceKey"
            :data-uiid="questionApplicant2TelNo.InterfaceKey"
            :hide-details="false"
            :rules="applicantPhoneNumberRules"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            aria-label="Phone number"
          />

          <BaseLabel text="Email address" class="mt-3 mb-2" />
          <BaseInput
            v-model="questionApplicant2EmailAddress.Value"
            :vid="questionApplicant2EmailAddress.InterfaceKey"
            :data-uiid="questionApplicant2EmailAddress.InterfaceKey"
            :hide-details="false"
            :rules="applicantEmailAddressRules"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            aria-label="Email address"
          />
        </template>

        <BaseLabel text="Your reference (optional)" class="mt-3 mb-2" />
        <BaseInput
          v-model="questionYourRef.Value"
          :vid="questionYourRef.InterfaceKey"
          :data-uiid="questionYourRef.InterfaceKey"
          :hide-details="false"
          :rules="yourRefRules"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          aria-label="Your reference (optional)"
        />

        <BaseLabel
          text="When would you like your policy to start?"
          class="mt-3 mb-2"
        />
        <BaseInputDate
          v-model="questionPolicyStartDate.Value"
          dataUiid="PolicyStartDate"
          :vid="questionPolicyStartDate.InterfaceKey"
          :rules="policyStartDateRules"
          :hide-details="false"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
        />

        <BaseButton
          rounded
          class="mt-3 font-weight-bold continue-button primary--text"
          height="60"
          type="submit"
          :text="
            $vuetify.breakpoint.lgAndUp ? 'CONTINUE TO PAYMENT' : 'CONTINUE'
          "
          :color="'psorange'"
          :fontSize="'font-20'"
          :min-width="$vuetify.breakpoint.lgAndUp ? '390' : '100%'"
        />
      </form>
    </ValidationObserver>
  </div>
</template>

<script>
import { EventBus } from "@/common/eventbus.js";
import {
  addFieldLabel,
  convertToBool,
  scrollTop,
  tryGoToNextStep,
} from "@/common/utils";
import LayoutAddressMixin from "@/Mixins/LayoutAddressMixin";
import { ValidationObserver } from "vee-validate";
import { mapActions, mapState, mapGetters } from "vuex";
import { addressSelected } from "@/common/addresses";
import { DOC_DELIVERY_PREF } from "@/constants/common.constants";
import {
  BASIS_OF_ADVICE,
  setItemDataUiids,
} from "@/constants/component.constants.js";
import * as LABEL_CONSTANTS from "@/constants/label.constants";
import * as ERROR_MESSAGES from "@/constants/message.constants.js";
import {
  DATE_REGEX,
  EMAIL_REGEX,
  USER_REF_EXCLUDE_ANGLE_BRACKETS_REGEX,
} from "@/constants/regex-patterns.constants";
import { ROUTE_NAME } from "@/constants/route.constants";
import {
  APPLICATION_MODULE,
  QUESTIONS_MODULE,
} from "@/store/types/module-types";

import BaseAddressLookup from "@/components/BaseComponents/BaseAddressLookup";
import BaseButton from "@/components/BaseComponents/BaseButton.vue";
import BaseInput from "@/components/BaseComponents/BaseInput.vue";
import BaseInputDate from "@/components/BaseComponents/BaseInputDate";
import BaseLabel from "@/components/BaseComponents/BaseLabel.vue";
import BaseSelect from "@/components/BaseComponents/BaseSelect";
import BaseToggleButton from "@/components/BaseComponents/BaseToggleButton";

export default {
  name: "TenantsLiabilityYourApplicationPage",
  mixins: [LayoutAddressMixin],
  components: {
    ValidationObserver,
    BaseButton,
    BaseLabel,
    BaseSelect,
    BaseInput,
    BaseInputDate,
    BaseToggleButton,
    BaseAddressLookup,
  },
  data() {
    return {
      questionBasisOfAdvice: {},
      questionDocumentDeliveryPreference: {},
      questionUseCorrespondenceAddress: {},
      questionApplicant1TelNo: {},
      questionApplicant1EmailAddress: {},
      questionApplicant2TelNo: {},
      questionApplicant2EmailAddress: {},
      questionPolicyStartDate: {},
      questionYourRef: {},
      LABEL_CONSTANTS,
      ERROR_MESSAGES,
      BASIS_OF_ADVICE,
      documentDeliveryPreferenceLocal: true,
      showSecondApplicantDetails: false,
      correspondenceAddress: {},
      isShowCorrespondenceAddressLabel: false,
      isEnteringAddressManually: false,
      DOC_DELIVERY_PREF,
      applicantPhoneNumberRules: addFieldLabel(
        {
          required: {},
          phone_number: {},
        },
        "Phone number"
      ),

      applicantEmailAddressRules: addFieldLabel(
        {
          required: {
            required: true,
          },
          max: {
            length: 50,
            errorMessageTemplate: ERROR_MESSAGES.ERROR_MSG_MAX_CHARACTER,
          },
          regex: {
            pattern: EMAIL_REGEX,
            customMessage: ERROR_MESSAGES.ERROR_INVALID_EMAIL_ADDRESS,
          },
        },
        "Email address"
      ),

      yourRefRules: addFieldLabel(
        {
          max: {
            length: 40,
            errorMessageTemplate: ERROR_MESSAGES.ERROR_MSG_MAX_CHARACTER,
          },
          regex: {
            pattern: USER_REF_EXCLUDE_ANGLE_BRACKETS_REGEX,
            customMessage: ERROR_MESSAGES.ERROR_MSG_USER_REF,
          },
        },
        "Your reference"
      ),
    };
  },
  watch: {
    documentDeliveryPreferenceLocal(val) {
      this.questionDocumentDeliveryPreference.Value = val
        ? DOC_DELIVERY_PREF.Online
        : DOC_DELIVERY_PREF.Postal;
      if (
        this.questionDocumentDeliveryPreference.Value ===
        DOC_DELIVERY_PREF.Online
      ) {
        this.applicationQuestionMap.UseCorrespondenceAddress.Value = false;
      }
    },
    "questionYourRef.Value"(value) {
      this.setUserReference(value);
    },
    "questionUseCorrespondenceAddress.Value"(value) {
      if (!value) {
        this.resetAddressForm();
        this.isShowCorrespondenceAddressLabel = false;
      }
    },
  },
  computed: {
    ...mapState(APPLICATION_MODULE, {
      applicationQuestionMap: "questionMap",
    }),
    ...mapState(QUESTIONS_MODULE, {
      questionsQuestionMap: "questionMap",
      userReference: "userReference",
      restoringLastStep: "restoringLastStep",
    }),
    ...mapGetters(APPLICATION_MODULE, ["selectedQuote"]),
    basisOfAdviceItems() {
      return setItemDataUiids("BasisOfAdvice", BASIS_OF_ADVICE);
    },
    questionDocumentDeliveryPreferenceIsPostal() {
      return (
        this.questionDocumentDeliveryPreference.Value ===
        DOC_DELIVERY_PREF.Postal
      );
    },
    policyStartDateRules() {
      return addFieldLabel(
        {
          required: {
            required: true,
          },
          date_valid: {
            errorMessageTemplate: ERROR_MESSAGES.ERROR_INVALID,
          },
          regex: {
            errorMessageTemplate: ERROR_MESSAGES.ERROR_INVALID,
            pattern: DATE_REGEX,
          },
          between_now_and: {
            future: this.selectedQuote?.QuoteExpiryDate,
          },
        },
        "Policy start date"
      );
    },
  },
  beforeMount() {
    this.onSetupAndLoadSavedData();
    this.initAddressForm();
    scrollTop();
  },
  mounted() {
    this.setCurrentRefObserverForm(this.$refs.observerFormApplication);

    if (!this.restoringLastStep) {
      return;
    }

    setTimeout(async () => {
      tryGoToNextStep(
        this.$refs.observerFormApplication,
        async (form) => await form.validate(),
        ROUTE_NAME.TENANTS_LIABILITY.B2B.PAYMENT
      );
    }, 0);
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, [
      "setUserReference",
      "refreshSessionAndNextStep",
      "toggleGlobalSaveAndExitDialog",
      "setCurrentRefObserverForm",
    ]),
    addressSelected,
    async onSubmit(validate) {
      const isFormValid = await validate();
      if (!isFormValid) {
        EventBus.$emit("validation-error", "Validation error occurred");
        return;
      }

      this.refreshSessionAndNextStep(ROUTE_NAME.TENANTS_LIABILITY.B2B.PAYMENT);
    },
    backToYourQuotesStep() {
      this.$router.push({
        name: ROUTE_NAME.TENANTS_LIABILITY.B2B.QUOTATION_RESULT,
      });
    },
    onSetupAndLoadSavedData() {
      this.questionBasisOfAdvice = this.applicationQuestionMap.BasisOfAdvice;
      if (!this.questionBasisOfAdvice.Value) {
        this.questionBasisOfAdvice.Value = "NonAdvised";
      }

      this.questionDocumentDeliveryPreference =
        this.applicationQuestionMap.DocumentDeliveryPreference;
      this.setInitialDocDeliveryPref();

      this.questionUseCorrespondenceAddress =
        this.applicationQuestionMap.UseCorrespondenceAddress;
      if (this.questionUseCorrespondenceAddress) {
        this.questionUseCorrespondenceAddress.Value = convertToBool(
          this.questionUseCorrespondenceAddress.Value
        );
      }

      this.questionApplicant1TelNo =
        this.applicationQuestionMap.Applicant1TelNo;

      this.questionApplicant1EmailAddress =
        this.applicationQuestionMap.Applicant1EmailAddress;

      const secondApplicantIncluded =
        this.questionsQuestionMap.SecondApplicantIncluded;
      if (
        secondApplicantIncluded &&
        convertToBool(secondApplicantIncluded.Value)
      ) {
        this.showSecondApplicantDetails = true;
      }

      this.questionApplicant2TelNo =
        this.applicationQuestionMap.Applicant2TelNo;
      this.questionApplicant2EmailAddress =
        this.applicationQuestionMap.Applicant2EmailAddress;

      if (!this.showSecondApplicantDetails) {
        this.questionApplicant2TelNo.Value = null;
        this.questionApplicant2EmailAddress.Value = null;
      }

      this.questionPolicyStartDate =
        this.applicationQuestionMap.PolicyStartDate;

      this.questionYourRef = this.applicationQuestionMap.YourRef;
      if (this.userReference) {
        this.questionYourRef.Value = this.userReference;
      }
    },
    setInitialDocDeliveryPref() {
      if (!this.questionDocumentDeliveryPreference.Value) {
        this.questionDocumentDeliveryPreference.Value =
          DOC_DELIVERY_PREF.Online; // default to Online
      } else if (
        this.questionDocumentDeliveryPreference.Value ===
        DOC_DELIVERY_PREF.Postal
      ) {
        this.documentDeliveryPreferenceLocal = false; // make the UI match Postal
      }
    },
    initAddressForm() {
      this.correspondenceAddress = {
        PostCode: this.applicationQuestionMap.CorrespondencePostCode,
        Street: this.applicationQuestionMap.CorrespondenceStreet,
        District: this.applicationQuestionMap.CorrespondenceDistrict,
        Town: this.applicationQuestionMap.CorrespondenceTown,
        County: this.applicationQuestionMap.CorrespondenceCounty,
      };
    },
    correspondenceAddressLabel(val) {
      this.isShowCorrespondenceAddressLabel = val;
    },
    resetAddressForm() {
      (this.applicationQuestionMap.CorrespondencePostCode.Value = ""),
        (this.applicationQuestionMap.CorrespondenceStreet.Value = ""),
        (this.applicationQuestionMap.CorrespondenceDistrict.Value = ""),
        (this.applicationQuestionMap.CorrespondenceTown.Value = ""),
        (this.applicationQuestionMap.CorrespondenceCounty.Value = "");
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(.find-property) {
  width: 100% !important;
  margin-top: 6px;
  .v-input__slot {
    height: 48px !important;
  }
}
@media screen and (min-width: $grid-breakpoints-lg) {
  :deep(.find-property) {
    max-width: 480px;
  }
  .sub-help-text {
    margin-top: 16px;
    max-width: 630px;
    font-size: 14px;
    color: #272727;
  }
}
</style>
